import {
  inject,
  Component,
  DestroyRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';

import { TranslateService } from '@ngx-translate/core';

import { UtilsService } from 'src/app/core/services/utils.service';
import { ObjectActionsService } from 'src/app/core/services/object-actions.service';
import { ObjectCreatorService } from 'src/app/core/services/object-creator.service';

import {
  Feed,
  Dimension,
  Dashboard,
  DataObject,
} from 'src/app/core/models/core.models';
import {
  NomObjectClass,
  NomObjectLifecycleStatus,
  SortDirection,
} from 'src/app/core/constants';

import {
  objectChanged,
  fetchWorkspaceObjects,
} from 'src/app/core/actions/core.actions';
import {
  selectObjects,
  clearSelectedObjects,
} from 'src/app/core/actions/selected.actons';
import {
  setWorkspaceSortingField,
  setWorkspaceFilterString,
  setWorkspaceObjectsLifeCycle,
  setWorkspaceSortingDirection,
  fetchWorkspaceViewConfig,
  toggleWorkspaceViewConfigOption,
} from 'src/app/core/actions/workspace.actions';

import {
  selectCoreLoading,
  selectSelectedObjects,
  selectWorkspaceFilterString,
  selectWorkspaceSortingField,
  selectWorkspaceSortingDirection,
  selectFeedsByWorkspaceAndStateWithOptions,
  selectDashboardsByWorkspaceAndStateWithOptions,
  selectDimensionsByWorkspaceAndStateWithOptions,
  selectUser,
  selectWorkspaceViewConfig,
  selectWorspaceObjectsLifeCycle,
} from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';

import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  take,
  tap,
} from 'rxjs';
import { PropertyViewConfig } from 'src/app/core/services/feed-settings.service';

/** A row in a projected toolbar dropdown panel (view options / sort). */
interface ToolbarMenuItem {
  text?: string;
  icon?: IconProp;
  value?: unknown;
  label?: string;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
}

/** A run of consecutive same-`radioGroup` rows, or a single toggle row. */
interface ToolbarMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: unknown;
  items: ToolbarMenuItem[];
}

/** A command row in the "apply to selected" dropdown (from ObjectActionsService). */
interface ApplyAction {
  text: string;
  icon?: IconProp;
  // eslint-disable-next-line @typescript-eslint/ban-types
  action?: Function; // matches ObjectActionsService.getButtonForAction's action
}

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.component.html',
  styleUrls: ['./workspace.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkspaceComponent {
  dashboards$: Observable<Array<Dashboard>>;
  feeds$: Observable<Array<Feed>>;
  dimensions$: Observable<Array<Dimension>>;
  loading$: Observable<boolean>;
  dashboards: Array<Dashboard>;
  feeds: Array<Feed>;
  dimensions: Array<Dimension>;

  currentTab: 'active' | 'archived' | 'deleted' = 'active';
  filterString: string;
  viewConfig: PropertyViewConfig[] = [];

  // * Toolbar state (migrated from WorkspaceActionsService)
  selectAllChecked = false;
  lifecycleStatus = 1;
  sortField = 'title';
  sortDirection: SortDirection = 'asc';
  viewItems: ToolbarMenuItem[] = [];
  viewGroups: ToolbarMenuGroup[] = [];
  applyActions: ApplyAction[] = [];
  applyCount = 0;

  private readonly statusLabels: Record<number, string> = {
    1: 'workspace-actions.button.active',
    3: 'workspace-actions.button.archived',
    4: 'workspace-actions.button.deleted',
  };
  private readonly statusIcons: Record<number, IconProp> = {
    1: ['fas', 'check'],
    3: ['fas', 'box-archive'],
    4: ['fas', 'trash'],
  };

  get sortDirIcon(): IconProp {
    return this.sortDirection === 'asc'
      ? ['fas', 'sort-amount-up']
      : ['fas', 'sort-amount-down'];
  }
  get sortFieldIcon(): IconProp {
    return this.sortField === 'time_created' ||
      this.sortField === 'time_modified'
      ? ['fas', 'clock']
      : ['fas', 'text-height'];
  }
  get statusLabel(): string {
    return this.translateService.instant(
      this.statusLabels[this.lifecycleStatus] ?? this.statusLabels[1],
    );
  }
  get statusIcon(): IconProp {
    return this.statusIcons[this.lifecycleStatus] ?? this.statusIcons[1];
  }
  get applyLabel(): string {
    return `${this.translateService.instant('workspace-actions.button.apply-to-selected')} (${this.applyCount})`;
  }
  get sortLabel(): string {
    return this.translateService.instant('workspace-actions.button.sort');
  }
  get viewLabel(): string {
    return this.translateService.instant('workspace-actions.button.view');
  }

  private destroyRef = inject(DestroyRef);

  public constructor(
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private objectCreatorService: ObjectCreatorService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    public utilService: UtilsService,
    public objectActionService: ObjectActionsService,
  ) {}

  ngOnInit() {
    this.store.dispatch(
      fetchWorkspaceObjects({
        workspaceId: this.route.snapshot.params['workspaceId'],
        lifecycleStatus: [NomObjectLifecycleStatus.ACTIVE],
      }),
    );

    this.dashboards$ = this.store.select(
      selectDashboardsByWorkspaceAndStateWithOptions,
    );
    this.feeds$ = this.store.select(selectFeedsByWorkspaceAndStateWithOptions);
    this.dimensions$ = this.store.select(
      selectDimensionsByWorkspaceAndStateWithOptions,
    );

    this.dashboards$
      .pipe(
        tap((dashboards) => {
          this.dashboards = dashboards;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.feeds$
      .pipe(
        tap((feeds) => {
          this.feeds = feeds;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.dimensions$
      .pipe(
        tap((dimensions) => {
          this.dimensions = dimensions;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.loading$ = this.store.select(selectCoreLoading);

    this.route.data
      .pipe(
        tap((data) => {
          if (data['workspace']) {
            this.store.dispatch(objectChanged({ object: data['workspace'] }));
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUser)
      .pipe(
        take(1),
        tap((user) => {
          this.store.dispatch(fetchWorkspaceViewConfig({ userId: user.id }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectWorkspaceViewConfig)
      .pipe(
        tap((config) => {
          this.viewConfig = config;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.setupToolbar();
  }

  public setupToolbar(): void {
    this.store
      .pipe(
        select(selectWorspaceObjectsLifeCycle),
        distinctUntilChanged(),
        tap((status) => {
          this.lifecycleStatus = this.statusLabels[status] ? status : 1;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    combineLatest([
      this.store.pipe(
        select(selectWorkspaceSortingDirection),
        distinctUntilChanged(),
      ),
      this.store.pipe(
        select(selectWorkspaceSortingField),
        distinctUntilChanged(),
      ),
    ])
      .pipe(
        tap(([dir, field]) => {
          this.sortDirection = dir;
          this.sortField = field;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectWorkspaceViewConfig)
      .pipe(
        tap((config) => {
          this.viewItems = (config ?? []).map((c) => ({
            text: c.label,
            label: c.label,
            icon: c.icon,
            value: c.path,
            isToggle: !c.isRadio,
            isRadio: c.isRadio,
            isSelected: c.selected,
            radioGroup: c.radioGroup,
          }));
          this.viewGroups = this.groupViewItems(this.viewItems);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .pipe(
        select(selectSelectedObjects),
        tap((selectedObjects) => {
          this.applyCount = selectedObjects.size;
          this.updateApplyActions(selectedObjects);
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectWorkspaceFilterString)
      .pipe(
        tap((filterString) => {
          this.filterString = filterString;
          this.cdr.markForCheck();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updateApplyActions(selectedObjects: Map<string, DataObject>): void {
    let applicable: string[] = [];
    selectedObjects.forEach((object) => {
      // Workspace excludes objects the user can't edit.
      if (object.can_edit === false) {
        return;
      }
      let classId = object.object_class_id;
      const typeId = object.object_type_id || null;
      // widget placeholders set object_class_id/object_id to the same value
      if (object['widget_type_id'] !== undefined) {
        classId = NomObjectClass.WIDGET;
      }
      const actions =
        this.objectActionService.objectActions[classId]?.[
          object.lifecycle_status
        ]?.[typeId] ?? null;
      if (actions) {
        const enabled = Object.keys(actions).filter((a) => actions[a] === true);
        applicable =
          applicable.length === 0
            ? enabled
            : applicable.filter((a) => enabled.includes(a));
      }
    });

    this.applyActions = applicable.map((action) => {
      const button = this.objectActionService.getButtonForAction(action);
      return {
        text: button.label,
        icon: button.leadingIcon,
        action: button.action,
      };
    });
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;
    this.selectAllObjects(selected);
  }

  onStatusChange(status: number): void {
    this.lifecycleStatus = status;
    const workspaceId = this.route.snapshot.params['workspaceId'];
    this.store.dispatch(
      setWorkspaceObjectsLifeCycle({
        workspaceId,
        workspaceObjectsLifeCycle: status,
      }),
    );
  }

  onSortFieldChange(sortingField: string): void {
    this.sortField = sortingField;
    this.store.dispatch(setWorkspaceSortingField({ sortingField }));
  }

  onSortDirectionChange(sortingDirection: SortDirection): void {
    this.sortDirection = sortingDirection;
    this.store.dispatch(setWorkspaceSortingDirection({ sortingDirection }));
  }

  onViewToggle(item: ToolbarMenuItem): void {
    this.store.dispatch(
      toggleWorkspaceViewConfigOption({ optionName: item.label }),
    );
  }

  /** Group consecutive same-`radioGroup` rows so each run gets one radiogroup wrapper. */
  private groupViewItems(items: ToolbarMenuItem[]): ToolbarMenuGroup[] {
    const groups: ToolbarMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: ToolbarMenuGroup, value: unknown): void {
    const item = group.items.find((i) => i.value === value);
    if (item) {
      this.onViewToggle(item);
    }
  }

  handleObjectSearchChange(query: string) {
    // Use the debounced function instead of the original search function

    if (typeof query == 'string') {
      this.filterString = query;
      this.store.dispatch(
        setWorkspaceFilterString({ filterString: this.filterString }),
      );
    }
  }

  createNewDashboard() {
    this.objectCreatorService.createNewDashboard();
  }

  createNewFeed() {
    this.objectCreatorService.createNewFeed();
  }

  createNewDimension() {
    this.objectCreatorService.createNewDimension();
  }

  selectAllObjects(selected: boolean) {
    const objects = [...this.dashboards, ...this.feeds, ...this.dimensions];
    if (selected) {
      this.store.dispatch(selectObjects({ objects }));
    } else {
      this.store.dispatch(clearSelectedObjects());
    }
  }

  trackById(index: number, object: DataObject): any {
    return object.id;
  }
}
